import { useCallback, useRef } from 'react'

/**
 * Overlay layer for the waveform. Renders one draggable vertical
 * handle per slice boundary and routes drag events back to the
 * parent via `onBoundariesChange`.
 *
 * A "boundary" is the time in seconds where one slice ends and the
 * next begins. An N-slice chop has N-1 interior boundaries plus the
 * implicit 0 and duration endpoints. The parent owns the sorted
 * boundary array; this component never mutates state directly.
 *
 * Collision guard: dragging a boundary past its neighbour is
 * silently clamped to neighbour - epsilon. Users don't want a
 * "this would delete your neighbour" confirmation mid-drag.
 *
 * Snap-to-grid: when `snapSec` is provided (derived from BPM +
 * current zoom's subdivision), drag positions quantise to the
 * nearest multiple. When undefined, drag is free-form (sample-
 * frame resolution).
 */

/** Minimum gap between adjacent boundaries in seconds. Keeps
 *  markers from fusing into each other and prevents zero-length
 *  slices that would be untriggerable. */
const MIN_SLICE_SEC = 0.01

interface SliceMarkersProps {
  /** Interior boundary times, seconds. Sorted ascending. */
  boundaries: number[]
  /** Full sample duration in seconds. */
  durationSec: number
  /** CSS pixel width of the overlay (matches the waveform). */
  width: number
  /** CSS pixel height of the overlay. */
  height: number
  /**
   * Snap resolution in seconds, e.g. `secPer16th` when zoomed
   * normal. Undefined → snap off (freely draggable).
   */
  snapSec?: number
  /** Called with the new sorted boundary array on every drag. */
  onBoundariesChange: (next: number[]) => void
}

export function SliceMarkers({
  boundaries,
  durationSec,
  width,
  height,
  snapSec,
  onBoundariesChange,
}: SliceMarkersProps) {
  // Tracks the index being dragged + the pointer-id that captured
  // it. Using a ref avoids a re-render on every pointermove.
  const dragRef = useRef<{ index: number; pointerId: number } | null>(null)

  const pxPerSec = durationSec > 0 ? width / durationSec : 0

  const clamp = useCallback(
    (index: number, desiredSec: number): number => {
      const prev = index === 0 ? 0 : boundaries[index - 1]
      const next =
        index === boundaries.length - 1 ? durationSec : boundaries[index + 1]
      const lo = prev + MIN_SLICE_SEC
      const hi = next - MIN_SLICE_SEC
      if (lo >= hi) return (prev + next) / 2
      let v = Math.max(lo, Math.min(hi, desiredSec))
      if (snapSec && snapSec > 0) {
        const snapped = Math.round(v / snapSec) * snapSec
        if (snapped >= lo && snapped <= hi) v = snapped
      }
      return v
    },
    [boundaries, durationSec, snapSec],
  )

  const handlePointerDown = (
    e: React.PointerEvent<HTMLDivElement>,
    index: number,
  ) => {
    ;(e.target as Element).setPointerCapture(e.pointerId)
    dragRef.current = { index, pointerId: e.pointerId }
    e.preventDefault()
  }

  const handlePointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    const drag = dragRef.current
    if (!drag || drag.pointerId !== e.pointerId) return
    const rect = e.currentTarget.getBoundingClientRect()
    const relX = e.clientX - rect.left
    const desiredSec = pxPerSec > 0 ? relX / pxPerSec : 0
    const clamped = clamp(drag.index, desiredSec)
    const next = boundaries.slice()
    next[drag.index] = clamped
    // Re-sort is a defence-in-depth — in principle `clamp` already
    // keeps ordering intact, but doing it here means one flaky
    // clamp call can't leave the array with a duplicate swap.
    next.sort((a, b) => a - b)
    onBoundariesChange(next)
  }

  const handlePointerUp = (e: React.PointerEvent<HTMLDivElement>) => {
    const drag = dragRef.current
    if (drag && drag.pointerId === e.pointerId) {
      dragRef.current = null
    }
  }

  return (
    <div
      style={{
        position: 'absolute',
        inset: 0,
        width,
        height,
      }}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerCancel={handlePointerUp}
    >
      {boundaries.map((sec, index) => {
        const left = sec * pxPerSec
        return (
          <div
            key={index}
            onPointerDown={e => handlePointerDown(e, index)}
            title={`Slice boundary ${index + 1} — drag to adjust`}
            style={{
              position: 'absolute',
              left: left - 6,
              top: 0,
              width: 12,
              height,
              cursor: 'ew-resize',
              touchAction: 'none',
            }}
          >
            {/* Thin visual line + a slightly wider hit area. */}
            <div
              style={{
                position: 'absolute',
                left: 5,
                top: 0,
                width: 2,
                height,
                background:
                  'linear-gradient(180deg, rgba(244, 114, 182, 0.95), rgba(244, 114, 182, 0.6))',
                pointerEvents: 'none',
              }}
            />
            <div
              style={{
                position: 'absolute',
                left: 1,
                top: height / 2 - 6,
                width: 10,
                height: 12,
                borderRadius: 3,
                background: 'rgba(244, 114, 182, 0.85)',
                pointerEvents: 'none',
              }}
            />
          </div>
        )
      })}
    </div>
  )
}
